import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import Pagination from '../../components/Pagination';
import {
  Eye, XCircle, AlertTriangle, History, Pencil, Check, Loader2, Download, Info, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../../lib/format';

const API = process.env.REACT_APP_BACKEND_URL;

const AMEND_LOCKED_STATES = ['Approved', 'Partially Received', 'Fully Received'];
const PAGE_SIZE = 50;

const getStatusColor = (status) => {
  switch (status) {
    case 'Draft': return 'bg-slate-100 text-slate-700';
    case 'Approved': return 'bg-indigo-50 text-[#3b4872]';
    case 'Partially Received': return 'bg-amber-50 text-amber-700';
    case 'Fully Received': return 'bg-emerald-50 text-emerald-700';
    default: return 'bg-slate-100 text-slate-700';
  }
};

// ========== PURCHASE ORDERS TAB (READ-ONLY IN STORES) ==========
// Create PO is intentionally NOT available here.
// Only the Procurement team can create Purchase Orders (see Procurement module).
export function PurchaseOrdersTab({ projectId }) {
  const [pos, setPOs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const requestSeq = useRef(0);

  const fetchPOs = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (projectId) params.set('project_id', projectId);
      if (search) params.set('q', search);
      params.set('page', String(page));
      params.set('limit', String(pageSize));
      const res = await fetch(`${API}/api/stores/pos?${params.toString()}`, { credentials: 'include' });
      if (seq !== requestSeq.current) return; // a newer request already landed — discard this stale response
      if (res.ok) {
        const data = await res.json();
        if (seq !== requestSeq.current) return;
        setPOs(data?.items || []);
        setTotal(data?.total ?? (data?.items || []).length);
      }
    } finally { if (seq === requestSeq.current) setLoading(false); }
  }, [projectId, search, page, pageSize]);

  // Reset to page 1 whenever the project or search term changes, so a stale
  // page-N request for the old filters is never fired alongside the new one.
  useEffect(() => { setPage(1); }, [projectId, search]);
  useEffect(() => { fetchPOs(); }, [fetchPOs]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const start = (page - 1) * pageSize;
  const end = Math.min(start + pos.length, total);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="text-sm text-gray-500" data-testid="po-tab-count">{total} Purchase Orders</div>
        <div className="relative w-64">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search PO number, vendor, item…"
            className="pl-8 text-sm h-9"
            data-testid="po-search-input"
          />
        </div>
        <div
          data-testid="stores-po-readonly-notice"
          className="flex items-center gap-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5"
        >
          <Info className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
          <span>Purchase Orders are <strong>view-only</strong> here. To create, approve, or amend a PO, go to{' '}
            <a href="/app/procurement" className="text-[#3b4872] underline font-semibold">Procurement &amp; Materials</a>.
          </span>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
            <table className="w-full text-sm sticky-col-header">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">PO Number</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Project</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Vendor</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Items</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Value</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Created</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">View</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-8 text-gray-500">Loading...</td></tr>
                ) : pos.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-8 text-gray-500">No purchase orders found</td></tr>
                ) : pos.map(po => (
                  <tr key={po.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <button onClick={() => setSelectedPO(po)} className="font-mono font-medium text-[#3b4872] underline text-xs hover:text-[#2d3856]">
                        {po.po_no}
                      </button>
                    </td>
                    <td className="px-4 py-3">{po.project_name}</td>
                    <td className="px-4 py-3">{po.vendor_name}</td>
                    <td className="px-4 py-3 text-right">{po.boq_items?.length || 0}</td>
                    <td className="px-4 py-3 text-right font-medium">₹{po.total_value?.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(po.status)}`}>{po.status}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{formatDate(po.created_at)}</td>
                    <td className="px-4 py-3 text-center">
                      <Button variant="ghost" size="sm" data-testid={`po-view-${po.id}`} onClick={() => setSelectedPO(po)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t">
            <Pagination
              page={page} pageSize={pageSize} total={total} start={start} end={end}
              onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
              label="purchase orders" data-testid="po-pagination-bar"
            />
          </div>
        </CardContent>
      </Card>

      {/* ── PO Detail Modal ─────────────────────────────────────────────── */}
      {selectedPO && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold font-mono">{selectedPO.po_no}</h2>
                  <p className="text-sm text-gray-500">{selectedPO.project_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm"
                    onClick={() => window.open(`${API}/api/po/${selectedPO.id}/pdf`, '_blank')}
                    data-testid="po-download-pdf-btn">
                    <Download className="w-4 h-4 mr-1" /> Download PDF
                  </Button>
                  <Button variant="ghost" onClick={() => setSelectedPO(null)}><XCircle className="w-5 h-5" /></Button>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div><span className="text-gray-500">Vendor:</span><p className="font-medium">{selectedPO.vendor_name}</p></div>
                <div><span className="text-gray-500">Status:</span>
                  <p className={`font-medium ${getStatusColor(selectedPO.status)} inline-block px-2 py-0.5 rounded mt-1`}>{selectedPO.status}</p>
                </div>
                <div><span className="text-gray-500">Total Value:</span><p className="font-bold text-lg">₹{selectedPO.total_value?.toLocaleString('en-IN')}</p></div>
              </div>

              {/* New fields */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Payment Terms:</span>
                  <p className="font-medium whitespace-pre-line mt-0.5">
                    {selectedPO.payment_terms || '—'}
                    {selectedPO.payment_days != null && <span className="text-slate-500"> ({selectedPO.payment_days} days)</span>}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Delivery Date:</span>
                  <p className="font-medium">{selectedPO.delivery_date || '—'}</p>
                </div>
                {selectedPO.shipping_address && (
                  <div>
                    <span className="text-gray-500">Shipping Address:</span>
                    <p className="font-medium whitespace-pre-line">{selectedPO.shipping_address}</p>
                  </div>
                )}
                {selectedPO.billing_address && (
                  <div>
                    <span className="text-gray-500">Billing Address:</span>
                    <p className="font-medium whitespace-pre-line">{selectedPO.billing_address}</p>
                  </div>
                )}
              </div>

              {/* Line items — full description, no truncation */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2">Item Code</th>
                      <th className="text-left px-3 py-2">Description</th>
                      <th className="text-center px-3 py-2">Unit</th>
                      <th className="text-right px-3 py-2">Qty</th>
                      <th className="text-right px-3 py-2">Rate</th>
                      <th className="text-right px-3 py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(selectedPO.boq_items || []).map((item, idx) => (
                      <tr key={idx} data-testid={`po-line-${idx}`} className={item.price_anomaly ? 'bg-amber-50' : ''}>
                        <td className="px-3 py-2 font-mono text-xs">{item.item_code}</td>
                        <td className="px-3 py-2">{item.description}</td>
                        <td className="px-3 py-2 text-center">{item.unit}</td>
                        <td className="px-3 py-2 text-right">{item.quantity}</td>
                        <td className="px-3 py-2 text-right">
                          ₹{item.rate?.toLocaleString('en-IN')}
                          {item.price_anomaly && (
                            <span data-testid={`po-anomaly-icon-${idx}`} title={item.anomaly_note} className="ml-1 inline-flex align-middle text-amber-600">
                              <AlertTriangle className="w-3.5 h-3.5" />
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">₹{item.amount?.toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(selectedPO.boq_items || []).some(i => i.price_anomaly) && (
                <div data-testid="po-anomaly-banner" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                  <div>
                    <span className="font-semibold">Price anomaly — review before payment:</span>
                    <ul className="list-disc ml-4 mt-1 space-y-0.5">
                      {(selectedPO.boq_items || []).filter(i => i.price_anomaly).map((i, k) => (
                        <li key={k}>{i.anomaly_note}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {selectedPO.terms_conditions && (
                <div className="text-sm">
                  <span className="text-gray-500 font-medium">Terms & Conditions:</span>
                  <p className="whitespace-pre-line mt-1 text-xs text-slate-700 bg-slate-50 rounded p-3 border">{selectedPO.terms_conditions}</p>
                </div>
              )}

              <POAmendmentHistory po={selectedPO} onAmended={(updated) => { setSelectedPO(updated); fetchPOs(); }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== PO AMENDMENT HISTORY ==========
export function POAmendmentHistory({ po, onAmended }) {
  const [amendments, setAmendments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [remarks, setRemarks] = useState('');
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState([]);

  const poId = po?.id;
  const isLocked = AMEND_LOCKED_STATES.includes(po?.status);

  const fetchAmendments = useCallback(async () => {
    if (!poId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/stores/po/${poId}/amendments`, { credentials: 'include' });

      if (res.ok) {
        const data = await res.json();
        setAmendments(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching amendments:', err);
    } finally {
      setLoading(false);
    }
  }, [poId]);

  useEffect(() => { fetchAmendments(); }, [fetchAmendments]);

  const openEditor = () => {
    setDeliveryDate((po.delivery_date || '').slice(0, 10));
    setPaymentTerms(po.payment_terms || '');
    setRemarks(po.remarks || '');
    setReason('');
    setLines((po.boq_items || []).map(li => ({
      boq_id: li.boq_id, item_code: li.item_code, description: li.description, unit: li.unit,
      quantity: li.quantity ?? 0, rate: li.rate ?? 0,
      orig_quantity: li.quantity ?? 0, orig_rate: li.rate ?? 0,
    })));
    setEditing(true);
  };

  const updateLine = (boq_id, field, value) => {
    setLines(prev => prev.map(l => (l.boq_id === boq_id ? { ...l, [field]: value } : l)));
  };

  const buildPayload = () => {
    const payload = {};
    const origDate = (po.delivery_date || '').slice(0, 10);
    if (deliveryDate !== origDate) payload.delivery_date = deliveryDate || null;
    if (paymentTerms !== (po.payment_terms || '')) payload.payment_terms = paymentTerms;
    if (remarks !== (po.remarks || '')) payload.remarks = remarks;
    const changedItems = [];
    lines.forEach(l => {
      const q = Number(l.quantity);
      const r = Number(l.rate);
      const item = { boq_id: l.boq_id };
      let changed = false;
      if (q !== Number(l.orig_quantity)) { item.quantity = q; changed = true; }
      if (r !== Number(l.orig_rate)) { item.rate = r; changed = true; }
      if (changed) changedItems.push(item);
    });
    if (changedItems.length) payload.items = changedItems;
    return payload;
  };

  const hasChanges = () => Object.keys(buildPayload()).length > 0;

  const handleSave = async () => {
    const payload = buildPayload();
    if (Object.keys(payload).length === 0) { toast.error('No changes to record'); return; }
    if (isLocked && !reason.trim()) { toast.error('A reason is required to amend an approved PO'); return; }
    payload.reason = reason.trim() || null;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/stores/po/${poId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(data.amendments_added
          ? `Amendment recorded (${data.amendments_added} change${data.amendments_added > 1 ? 's' : ''})`
          : 'No changes detected');
        setEditing(false);
        await fetchAmendments();
        if (data.po && onAmended) onAmended(data.po);
      } else {
        toast.error(data.detail || 'Failed to amend PO');
      }
    } catch { toast.error('Error amending PO'); } finally { setSaving(false); }
  };

  return (
    <div className="border-t pt-4" data-testid="po-amendment-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <History className="w-4 h-4" /> Amendment History
          {amendments.length > 0 && <span className="text-xs font-normal text-gray-400">({amendments.length})</span>}
        </div>
        {/* Amend button removed — PO amendments are only available in Procurement module */}
      </div>
      {loading ? (
        <p className="text-xs text-gray-400 py-2">Loading amendments…</p>
      ) : amendments.length === 0 ? (
        <p className="text-xs text-gray-400 py-2" data-testid="po-amend-empty">No amendments recorded yet.</p>
      ) : (
        <div className="space-y-2 mb-3" data-testid="po-amend-trail">
          {amendments.map((a) => (
            <div key={a.id} className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-2.5" data-testid={`po-amend-row-${a.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-800">{a.field_changed}</span>
                <span className="text-gray-400">{a.amended_at ? formatDateTime(a.amended_at) : ''}</span>
              </div>
              <div className="mt-1 text-gray-600">
                <span className="line-through text-rose-500">{String(a.old_value ?? '—')}</span>
                <span className="mx-1">→</span>
                <span className="text-emerald-600 font-medium">{String(a.new_value ?? '—')}</span>
              </div>
              <div className="mt-1 text-gray-400">by {a.amended_by || 'Unknown'}{a.reason ? ` · ${a.reason}` : ''}</div>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <div className="bg-indigo-50/40 border border-indigo-100 rounded-lg p-4 space-y-3" data-testid="po-amend-form">
          {isLocked && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              This PO is approved — every change is logged and a reason is mandatory.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Delivery Date</label>
              <Input type="date" data-testid="po-amend-delivery-date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Payment Terms</label>
              <Input data-testid="po-amend-payment-terms" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} className="text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Remarks</label>
            <Input data-testid="po-amend-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} className="text-sm" />
          </div>
          <div className="border rounded-lg overflow-hidden bg-white">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-2 py-1.5">Item</th>
                  <th className="text-right px-2 py-1.5 w-24">Qty</th>
                  <th className="text-right px-2 py-1.5 w-28">Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lines.map((l) => (
                  <tr key={l.boq_id} data-testid={`po-amend-line-${l.boq_id}`}>
                    <td className="px-2 py-1.5">
                      <div className="font-mono text-[10px] text-gray-500">{l.item_code}</div>
                      <div>{(l.description || '').substring(0, 36)}</div>
                    </td>
                    <td className="px-2 py-1.5">
                      <Input type="number" data-testid={`po-amend-qty-${l.boq_id}`} value={l.quantity}
                        onChange={(e) => updateLine(l.boq_id, 'quantity', e.target.value)} className="text-xs text-right h-8" />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input type="number" data-testid={`po-amend-rate-${l.boq_id}`} value={l.rate}
                        onChange={(e) => updateLine(l.boq_id, 'rate', e.target.value)} className="text-xs text-right h-8" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">
              Reason {isLocked && <span className="text-rose-500">*</span>}
            </label>
            <Input data-testid="po-amend-reason" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder={isLocked ? 'Required — why is this PO being amended?' : 'Optional'} className="text-sm" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" data-testid="po-amend-cancel" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            <Button size="sm" data-testid="po-amend-save" onClick={handleSave} disabled={saving || !hasChanges()}>
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
              Record Amendment
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
